/**
 * Copyright (C) BoostCommerce.net
 * This file is part of commercial BoostCommerce.net projects
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * Created on Tue Feb 14 2023 19:26:12
 */

// @flow
import _ from 'lodash';
import classnames from 'classnames';
import React, { Component } from 'react';
import { FormLayout, TextStyle, Link, Icon } from '@shopify/polaris';
import { CaretDownMinor, ExternalMinor } from '@shopify/polaris-icons';
import { Field } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { DropdownIndicator, styles } from './react-select-style';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderSelect2 } from 'src/components/views/common/forms/render-select-2-v2';
import { PAGINATION_ALIGNMENT_OPTIONS } from 'src/constants/theme-settings.jsx';
import { Collapse } from 'react-collapse';
import {
  PAGINATION_TYPE_OPTIONS,
  PAGINATION_TEXT_DESCRIPTION_OPTIONS,
  PAGINATION_PRODUCT_COUNT_POSITION_OPTIONS,
  NUMBER_SHAPE_OPTIONS,
  BUTTON_STYLE_TYPE_OPTIONS,
  BUTTON_STYLE_SHAPE_OPTIONS_1,
  BUTTON_STYLE_SHAPE_OPTIONS_2,
  BUTTON_STYLE_TYPE,
  PAGINATION_TYPE,
  MAX_FONT_SIZE_PAGINATION
} from 'src/constants/theme-settings.js';
import { RenderRadioButton2 } from 'src/components/views/common/forms/render-radio-button-2';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import RenderColorPickerItem from 'src/components/views/common/render-color-picker-item';
import TextForm from './text-form';
import { nonXSSv2 } from 'src/helpers/form-validation-helper';
import { checkHasXss } from 'src/helpers';
import { URL } from 'src/constants/links';

type Props = {
  formValues: any,
  themeSettingDefaultValue: any,
  themeSettingDefaultThemeValue: any,
  updateField: (field: string, value: any) => void
};

type ComposedProps = Props & WithI18nProps;

const nonXSSValue = nonXSSv2();

class PaginationForm extends Component<ComposedProps> {
  state = {
    isOpenedTextStyle: true,
    isOpenedButtonStyle: true
  };

  handleToggleCollapse = name => {
    this.setState({
      [name]: !this.state[name]
    });
  };

  handleUnmountInput = fieldName => {
    const { formValues, updateField } = this.props;
    const text = _.get(formValues, fieldName, '');
    if (checkHasXss(text)) {
      updateField(fieldName, '');
    }
  };

  handleChangePaginationType = type => {
    // Apply change value when update pagination type
    const { prefix, updateField, themeSettingDefaultValue, themeSettingDefaultThemeValue } =
      this.props;
    const [prefixMenuItem] = prefix.split('_');
    const keySetting = `${prefixMenuItem}_${type}`;
    const setting =
      themeSettingDefaultValue[keySetting] || themeSettingDefaultThemeValue[keySetting];

    updateField(prefix, setting);
  };

  /**
   * Build color pickers from form values redux
   * @param {*} formValues
   * @returns
   */
  getNumberStyleColorPickers = prefix => {
    const { i18n, formValues } = this.props;
    return [
      {
        colorValue: _.get(formValues, `${prefix}.color`),
        fieldName: `${prefix}.color`,
        fieldLabel: i18n.translate(
          'AdminHTML.ThemeSetting.Form.pagination_PageNumberStyle_Color_Item1'
        )
      },
      {
        colorValue: _.get(formValues, `${prefix}.colorOnSelected`),
        fieldName: `${prefix}.colorOnSelected`,
        fieldLabel: i18n.translate(
          'AdminHTML.ThemeSetting.Form.pagination_PageNumberStyle_Color_Item2'
        )
      },
      {
        colorValue: _.get(formValues, `${prefix}.backgroundColor`),
        fieldName: `${prefix}.backgroundColor`,
        fieldLabel: i18n.translate(
          'AdminHTML.ThemeSetting.Form.pagination_PageNumberStyle_Color_Item3'
        )
      },
      {
        colorValue: _.get(formValues, `${prefix}.backgroundColorOnSelected`),
        fieldName: `${prefix}.backgroundColorOnSelected`,
        fieldLabel: i18n.translate(
          'AdminHTML.ThemeSetting.Form.pagination_PageNumberStyle_Color_Item4'
        )
      }
    ];
  };

  /**
   * Build color pickers from form values redux
   * @param {*} formValues
   * @returns
   */
  getButtonStyleColorPickers = prefix => {
    const { i18n, formValues } = this.props;

    const paginationType = _.get(formValues, `${prefix}.paginationType`);

    if (paginationType === PAGINATION_TYPE.LOAD_MORE) {
      return [
        {
          colorValue: _.get(formValues, `${prefix}.button.color`),
          fieldName: `${prefix}.button.color`,
          fieldLabel: i18n.translate(
            'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_Color_Item1'
          )
        },
        {
          colorValue: _.get(formValues, `${prefix}.button.backgroundColor`),
          fieldName: `${prefix}.button.backgroundColor`,
          fieldLabel: i18n.translate(
            'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_Color_Item2'
          )
        },
        {
          colorValue: _.get(formValues, `${prefix}.button.borderColor`),
          fieldName: `${prefix}.button.borderColor`,
          fieldLabel: i18n.translate(
            'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_Color_Item4'
          )
        }
      ];
    }

    return [
      {
        colorValue: _.get(formValues, `${prefix}.button.color`),
        fieldName: `${prefix}.button.color`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_Color_Item1')
      },
      {
        colorValue: _.get(formValues, `${prefix}.button.backgroundColor`),
        fieldName: `${prefix}.button.backgroundColor`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_Color_Item2')
      },
      {
        colorValue: _.get(formValues, `${prefix}.button.backgroundColorOnHover`),
        fieldName: `${prefix}.button.backgroundColorOnHover`,
        fieldLabel: i18n.translate('AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_Color_Item3')
      }
    ];
  };

  render() {
    const { i18n, prefix, updateField, formValues, labelTranslation } = this.props;
    const { isOpenedTextStyle, isOpenedButtonStyle } = this.state;

    const paginationType = _.get(formValues, `${prefix}.paginationType`);
    const isPageLink = paginationType === PAGINATION_TYPE.PAGE_LINKS;
    const isLoadMore = paginationType === PAGINATION_TYPE.LOAD_MORE;

    const productCountChecked = _.get(formValues, `${prefix}.productCount.showProductCount`);

    const paginationTypeOptions = PAGINATION_TYPE_OPTIONS.map(option => {
      const { label, value } = option;
      return { label: i18n.translate(label), value };
    });

    const textDescriptionOptions = PAGINATION_TEXT_DESCRIPTION_OPTIONS.map(option => {
      const { label, value } = option;
      return {
        label: i18n.translate(label, { t1: '{{from}}', t2: '{{to}}', t3: '{{total}}' }),
        value
      };
    });

    const numberShapeOptions = NUMBER_SHAPE_OPTIONS.map(option => {
      const { label, value } = option;
      return { label: i18n.translate(label), value };
    });

    const buttonTypeOptions = BUTTON_STYLE_TYPE_OPTIONS.map(option => {
      const { label, value } = option;
      return { label: i18n.translate(label), value };
    });

    const buttonShapeOptions1 = BUTTON_STYLE_SHAPE_OPTIONS_1.map(option => {
      const { label, value } = option;
      return { label: i18n.translate(label), value };
    });

    const buttonShapeOptions2 = BUTTON_STYLE_SHAPE_OPTIONS_2.map(option => {
      const { label, value } = option;
      return { label: i18n.translate(label), value };
    });

    const buttonType = _.get(formValues, `${prefix}.button.buttonType`);
    const isTextWithIcon = buttonType === BUTTON_STYLE_TYPE.TEXT_WITH_ICON;
    const isTextOnly = buttonType === BUTTON_STYLE_TYPE.TEXT_ONLY;
    const isIconOnly = buttonType === BUTTON_STYLE_TYPE.ICON_ONLY;

    return (
      <FormLayout>
        <div>
          <span className="form-link">
            <TextStyle variation="subdued">
              {i18n.translate('AdminHTML.ThemeSetting.Form.pagination_UserGuide')}
            </TextStyle>{' '}
            <Link url={URL['PaginationThemeSetting']} external>
              {i18n.translate('AdminHTML.ThemeSetting.Common.clickHere')}
              <Icon source={ExternalMinor} color="base" />
            </Link>
            <span style={{ color: '#2C6ECB' }}>.</span>
          </span>
        </div>

        <div className="form-section">
          <div className="form-section__item">
            <Field
              name={`${prefix}.paginationType`}
              label={i18n.translate('AdminHTML.ThemeSetting.Form.pagination_PaginationType_Label')}
              options={paginationTypeOptions}
              component={RenderSelect2}
              components={{ DropdownIndicator }}
              styles={styles}
              isSearchable={false}
              onChange={this.handleChangePaginationType}
            />
          </div>

          {(isPageLink || isLoadMore) && (
            <div className="form-section__item form-section__item--medium-spacing">
              <p className="title">
                {i18n.translate('AdminHTML.ThemeSetting.Form.pagination_PaginationAlignment_Label')}
              </p>
              <div className="radio-button-wrapper">
                {PAGINATION_ALIGNMENT_OPTIONS.map((item, index) => (
                  <Field
                    key={index}
                    name={`${prefix}.alignment`}
                    id={item.value}
                    component={RenderRadioButton2}
                    label={item.icon}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="form-section__item form-section__item--standard-spacing">
            <Field
              name={`${prefix}.productCount.showProductCount`}
              component={RenderCheckbox}
              label={i18n.translate(
                'AdminHTML.ThemeSetting.Form.pagination_ProductCountCheckbox_Label'
              )}
            />
          </div>
          {productCountChecked && (
            <div className="form-section__item">
              <Field
                name={`${prefix}.textDescription`}
                label={i18n.translate(
                  'AdminHTML.ThemeSetting.Form.pagination_TextDescription_Label'
                )}
                options={textDescriptionOptions}
                component={RenderSelect2}
                components={{ DropdownIndicator }}
                styles={styles}
                isSearchable={false}
                // props={{
                //   textHelper: i18n.translate(
                //     'AdminHTML.ThemeSetting.Form.pagination_TextDescription_HelpText',
                //     { t1: '{{from}}', t2: '{{to}}', t3: '{{total}}' }
                //   )
                // }}
              />
            </div>
          )}
          {productCountChecked && (isPageLink || isLoadMore) && (
            <div className="form-section__item">
              <p>
                {i18n.translate(
                  'AdminHTML.ThemeSetting.Form.pagination_ProductCountPosition_Label'
                )}
              </p>
              <div
                className="radio-button-wrapper radio-button-wrapper--no-gap radio-button-wrapper--bordered"
                style={{ marginTop: '5px' }}>
                {PAGINATION_PRODUCT_COUNT_POSITION_OPTIONS.map((item, index) => (
                  <Field
                    key={index}
                    name={`${prefix}.productCount.position`}
                    id={item.value}
                    component={RenderRadioButton2}
                    label={i18n.translate(item.label)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: '20px' }}>
          {/* PAGE NUMBER STYLE FOR PAGE LINK */}
          {isPageLink && (
            <div className="form-section form-section--bordered">
              <div
                className="section-title-collapse__wrapper"
                onClick={() => this.handleToggleCollapse('isOpenedTextStyle')}>
                <p className="section-title">
                  {i18n.translate('AdminHTML.ThemeSetting.Form.pagination_PageNumberStyle_Header')}
                </p>
                <span className={classnames('caret', { collapsed: !isOpenedTextStyle })}>
                  <Icon source={CaretDownMinor} color="base" />
                </span>
              </div>
              <Collapse isOpened={isOpenedTextStyle}>
                <div className="form-section">
                  <div className="form-section__item">
                    <Field
                      name={`${prefix}.number.shape`}
                      label={i18n.translate(
                        'AdminHTML.ThemeSetting.Form.pagination_PageNumberStyle_NumberShape_Label'
                      )}
                      options={numberShapeOptions}
                      component={RenderSelect2}
                      components={{ DropdownIndicator }}
                      styles={styles}
                      isSearchable={false}
                    />
                  </div>
                  <div className="form-section" style={{ marginTop: '12px' }}>
                    <p className="title title-small">
                      {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
                    </p>
                    {this.getNumberStyleColorPickers(`${prefix}.number`).map((item, index) => (
                      <RenderColorPickerItem
                        key={index}
                        colorValue={item.colorValue}
                        fieldName={item.fieldName}
                        fieldLabel={item.fieldLabel}
                        updateField={updateField}
                      />
                    ))}
                  </div>
                  <TextForm
                    formValues={formValues}
                    updateField={updateField}
                    prefix={`${prefix}.number`}
                    maxFontSize={MAX_FONT_SIZE_PAGINATION}
                  />
                </div>
              </Collapse>
            </div>
          )}

          {/* BUTTON STYLE FOR PAGE LINK */}
          {isPageLink && (
            <div className="form-section form-section--bordered">
              <div
                className="section-title-collapse__wrapper"
                style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
                onClick={() => this.handleToggleCollapse('isOpenedButtonStyle')}>
                <p className="section-title">
                  {i18n.translate('AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_Header')}
                </p>
                <span className={classnames('caret', { collapsed: !isOpenedButtonStyle })}>
                  <Icon source={CaretDownMinor} color="base" />
                </span>
              </div>
              <Collapse isOpened={isOpenedButtonStyle}>
                <div className="form-section">
                  {/* BUTTON TYPE */}
                  <div className="form-section__item">
                    <Field
                      name={`${prefix}.button.buttonType`}
                      label={i18n.translate(
                        'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_ButtonType_Label'
                      )}
                      options={buttonTypeOptions}
                      component={RenderSelect2}
                      components={{ DropdownIndicator }}
                      styles={styles}
                      isSearchable={false}
                    />
                  </div>
                  {/* BUTTON SHAPE */}
                  {isIconOnly && (
                    <div className="form-section__item">
                      <Field
                        name={`${prefix}.button.shape`}
                        label={i18n.translate(
                          'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_ButtonShape_Label'
                        )}
                        options={buttonShapeOptions1}
                        component={RenderSelect2}
                        components={{ DropdownIndicator }}
                        styles={styles}
                        isSearchable={false}
                      />
                    </div>
                  )}
                  {(isTextOnly || isTextWithIcon) && (
                    <>
                      <div className="form-section__item">
                        <Field
                          name={`${prefix}.button.previousText`}
                          component={RenderTextField}
                          label={i18n.translate(
                            'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_PreviousText_Label'
                          )}
                          placeholder={
                            labelTranslation?.prevTextV2?.value ||
                            i18n.translate(
                              'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_PreviousText_Placeholder'
                            )
                          }
                          validate={[nonXSSValue]}
                          onUnmount={() => this.handleUnmountInput(`${prefix}.button.previousText`)}
                        />
                      </div>
                      <div className="form-section__item">
                        <Field
                          name={`${prefix}.button.nextText`}
                          component={RenderTextField}
                          label={i18n.translate(
                            'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_NextText_Label'
                          )}
                          placeholder={
                            labelTranslation?.nextTextV2?.value ||
                            i18n.translate(
                              'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_NextText_Placeholder'
                            )
                          }
                          validate={[nonXSSValue]}
                          onUnmount={() => this.handleUnmountInput(`${prefix}.button.nextText`)}
                        />
                      </div>
                    </>
                  )}
                  {/* only show when button type valued */}
                  {buttonType && (
                    <div className="form-section" style={{ marginTop: '12px' }}>
                      <p className="title title-small">
                        {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
                      </p>
                      {this.getButtonStyleColorPickers(prefix).map((item, index) => (
                        <RenderColorPickerItem
                          key={index}
                          colorValue={item.colorValue}
                          fieldName={item.fieldName}
                          fieldLabel={item.fieldLabel}
                          updateField={updateField}
                        />
                      ))}
                    </div>
                  )}
                  {(isTextOnly || isTextWithIcon) && (
                    <TextForm
                      formValues={formValues}
                      updateField={updateField}
                      prefix={`${prefix}.button`}
                      maxFontSize={MAX_FONT_SIZE_PAGINATION}
                      showTextTransform
                    />
                  )}
                </div>
              </Collapse>
            </div>
          )}

          {/* BUTTON STYLE FOR LOAD MORE */}
          {isLoadMore && (
            <div className="form-section form-section--bordered">
              <div
                className="section-title-collapse__wrapper"
                style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
                onClick={() => this.handleToggleCollapse('isOpenedButtonStyle')}>
                <p className="section-title">
                  {i18n.translate('AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_Header')}
                </p>
                <span className={classnames('caret', { collapsed: !isOpenedButtonStyle })}>
                  <Icon source={CaretDownMinor} color="base" />
                </span>
              </div>
              <Collapse isOpened={isOpenedButtonStyle}>
                <div className="form-section">
                  <div className="form-section__item">
                    <Field
                      name={`${prefix}.button.shape`}
                      label={i18n.translate(
                        'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_ButtonShape_Label'
                      )}
                      options={buttonShapeOptions2} // CHANGE
                      component={RenderSelect2}
                      components={{ DropdownIndicator }}
                      styles={styles}
                      isSearchable={false}
                    />
                  </div>
                  <div className="form-section__item">
                    <Field
                      name={`${prefix}.button.loadMoreText`}
                      component={RenderTextField}
                      label={i18n.translate(
                        'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_LoadMoreText_Label'
                      )}
                      placeholder={
                        labelTranslation?.loadMoreTextV2?.value ||
                        i18n.translate(
                          'AdminHTML.ThemeSetting.Form.pagination_ButtonStyle_LoadMoreText_Placeholder'
                        )
                      }
                      validate={[nonXSSValue]}
                      onUnmount={() => this.handleUnmountInput(`${prefix}.button.loadMoreText`)}
                    />
                  </div>
                  <div className="form-section" style={{ marginTop: '12px' }}>
                    <p className="title title-small">
                      {i18n.translate('AdminHTML.ThemeSetting.Form.color_Title')}
                    </p>
                    {this.getButtonStyleColorPickers(prefix).map((item, index) => (
                      <RenderColorPickerItem
                        key={index}
                        colorValue={item.colorValue}
                        fieldName={item.fieldName}
                        fieldLabel={item.fieldLabel}
                        updateField={updateField}
                      />
                    ))}
                  </div>
                  <TextForm
                    formValues={formValues}
                    updateField={updateField}
                    prefix={`${prefix}.button`}
                    maxFontSize={MAX_FONT_SIZE_PAGINATION}
                    showTextTransform
                  />
                </div>
              </Collapse>
            </div>
          )}
        </div>
      </FormLayout>
    );
  }
}

export default withI18n()(PaginationForm);
